import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  requestListResource,
  useResourceDispatch,
  useResourceState,
} from "../../Context";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
  CheckIcon,
  ChevronDownIcon,
  SelectorIcon,
} from "@heroicons/react/solid";
import {
  classNames,
  getErrorMessages,
  getResourceFileName,
  getWebinarFileName,
} from "../../Utils";
import { ErrorList } from "../../Components/ErrorList";
import { EmptyList } from "../../Components/EmptyList";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { Modal } from "../../Components/Modal";
import { DocumentIcon } from "@heroicons/react/outline";
import { downloadData, getUrl } from "aws-amplify/storage";

const STATUS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

const ResourceList = () => {
  const { t } = useTranslation();
  const dispatch = useResourceDispatch();
  const { resourceList, resourceListError, resourceListLoading } =
    useResourceState();

  const [selectedStatus, setselectedStatus] = useState(STATUS[0]);
  const [selectedfileDownload, setselectedfileDownload] = useState(null);

  const [downloading, setdownloading] = useState(false);
  const [downloadProgress, setdownloadProgress] = useState(0);
  const downloadRef = useRef(null);
  const [downloadDocError, setdownloadDocError] = useState(null);
  const [showDownloadErrorModal, setshowDownloadErrorModal] = useState(false);

  useEffect(() => {
    if (selectedStatus?.value) {
      requestListResource(dispatch, {
        status: selectedStatus.value,
      });
    }
  }, [dispatch, selectedStatus?.value]);

  useEffect(() => {
    if (!downloadDocError) {
      setshowDownloadErrorModal(false);
    } else {
      setshowDownloadErrorModal(true);
    }
  }, [downloadDocError]);

  useEffect(() => {
    if (selectedfileDownload?.key) {
      downloadFile(selectedfileDownload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedfileDownload?.key]);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    if (!totalBytes) {
      setdownloadProgress(0);
    } else {
      setdownloadProgress({ loaded: transferredBytes, total: totalBytes });
    }
  };

  const downloadFile = async (file) => {
    try {
      setdownloading(true);

      const request = downloadData({
        path: `protected/${file.identityId}/${file.key}`,
        options: {
          useAccelerateEndpoint: true,
          onProgress: onDownloadProgress,
        },
      });

      downloadRef.current = request;
      const result = await request.result;
      const fileName = getResourceFileName(file.key);
      
      downloadBlob(result.body, fileName);
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(file);
      }
    } finally {
      setdownloading(false);
      setselectedfileDownload(null);
    }
  };

  const handleDownloadError = async (file) => {
    try {
      const urlResult = await getUrl({
        path: `protected/${file.identityId}/${file.key}`,
        options: {
          useAccelerateEndpoint: true,
        },
      });
      const signedURL = urlResult.url.href;
      window.open(signedURL);
    } catch (signedURLError) {
      setdownloadDocError(signedURLError);
    }
  };

  const renderProgressBar = (downloadProgress) => {
    const total = downloadProgress?.total || 1;
    const percentage = Math.round(
      ((downloadProgress?.loaded || 0) / total) * 100
    );

    return (
      <div
        className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
        style={{ width: `${percentage}%` }}
      >
        {`${percentage}%`}
      </div>
    );
  };

  const renderFileModal = () => (
    <Modal
      title="Download File"
      show={downloading}
      onClose={() => {
        if (downloadRef.current) {
          const downloadTask = downloadRef.current;
          downloadTask.cancel();
        }
        setdownloading(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          <div className="flex-1 overflow-hidden">
            <div className="w-full">
              <p className="text-xs text-slate-500">{t("Downloading")}</p>
              <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
                {renderProgressBar(downloadProgress)}
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex flex-col justify-center items-center">
          <DocumentIcon className="h-24 w-24 text-slate-500" />
          <p className="text-xs font-medium">
            {Math.round(downloadProgress?.total / 1024 || 0)} KB
          </p>
        </div>
      </div>
    </Modal>
  );

  const renderDownloadError = () => {
    if (downloadDocError) {
      return (
        <ModalConfirm
          open={showDownloadErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setshowDownloadErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(downloadDocError)}
          onConfirm={() => {
            setdownloadDocError(null);
          }}
        />
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {renderFileModal()}
      {renderDownloadError()}
      <div>
        <Listbox value={selectedStatus} onChange={setselectedStatus}>
          {({ open }) => (
            <>
              <ListboxButton className="flex items-center justify-between w-full sm:w-48 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm text-sm">
                <span className="ml-3 mr-2">
                  {t(selectedStatus?.label || t("Select Status"))}
                </span>
                <SelectorIcon className="w-5 h-5 text-gray-500" />
              </ListboxButton>
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-48 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                  {STATUS.map((report) => (
                    <ListboxOption
                      key={report.value}
                      className={({ focus }) =>
                        classNames(
                          focus ? "text-white bg-blue-600" : "text-gray-900",
                          "cursor-default select-none relative py-2 pl-3 pr-9"
                        )
                      }
                      value={report}
                    >
                      {({ selected, focus }) => (
                        <>
                          <div className="flex items-center justify-between">
                            <span
                              className={classNames(
                                selected ? "font-semibold" : "font-normal",
                                "ml-3 mr-2 block truncate"
                              )}
                            >
                              {t(report.label)}
                            </span>
                            {selected ? (
                              <span
                                className={classNames(
                                  focus ? "text-white" : "text-blue-600",
                                  "absolute inset-y-0 right-0 flex items-center pr-4"
                                )}
                              >
                                <CheckIcon className="h-5 w-5" />
                              </span>
                            ) : null}
                          </div>
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </>
          )}
        </Listbox>
      </div>
      <div className="bg-white shadow-sm max-w-7xl mx-auto rounded-lg">
        {resourceListLoading ? (
          <div className="flex flex-col divide-y divide-gray-200">
            {Array(3)
              .fill()
              .map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse flex justify-between px-4 py-6 sm:px-6 gap-4"
                >
                  <div className="flex flex-col gap-2">
                    <div className="w-72 h-4 bg-gray-300 rounded-md"></div>
                    <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
                  </div>
                  <div className="self-center">
                    <div className="w-28 h-9 bg-gray-300 rounded-md"></div>
                  </div>
                </div>
              ))}
          </div>
        ) : resourceList.length > 0 ? (
          <div className="flex flex-col divide-y divide-gray-200">
            {resourceList.map((item) => {
              return (
                <div key={item.id} className="flex flex-col">
                  <div className="flex justify-between text-gray-900 px-4 py-6 sm:px-6">
                    <div className="flex flex-col">
                      <span className="font-medium">{item.title}</span>
                      <span>{item.category.name}</span>
                    </div>
                    <div className="self-center relative inline-block text-left">
                      <Listbox
                        value={selectedfileDownload}
                        onChange={setselectedfileDownload}
                      >
                        {({ open }) => (
                          <>
                            <ListboxButton className="flex items-center justify-between w-full sm:w-fit py-2 px-3 border border-blue-600 text-blue-600 bg-white rounded-md shadow-sm text-sm font-medium">
                              <span className="ml-3 mr-2">{t("Download")}</span>
                              <ChevronDownIcon className="w-5 h-5 text-blue-600" />
                            </ListboxButton>
                            <Transition
                              show={open}
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                            >
                              <ListboxOptions className="absolute right-0 z-10 mt-1 bg-white shadow-lg max-h-56 w-48 sm:w-auto rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                                {item?.files.map((file, index) => (
                                  <ListboxOption
                                    key={index}
                                    className={({ focus }) =>
                                      classNames(
                                        focus
                                          ? "text-white bg-blue-600"
                                          : "text-gray-900",
                                        "cursor-default select-none relative py-2 pl-3 pr-9"
                                      )
                                    }
                                    value={file}
                                  >
                                    {({ selected }) => (
                                      <>
                                        <div className="flex items-center justify-between">
                                          <span
                                            className={classNames(
                                              selected
                                                ? "font-semibold"
                                                : "font-normal",
                                              "ml-3 mr-2 block truncate"
                                            )}
                                          >
                                            {getWebinarFileName(file.key)}
                                          </span>
                                        </div>
                                      </>
                                    )}
                                  </ListboxOption>
                                ))}
                              </ListboxOptions>
                            </Transition>
                          </>
                        )}
                      </Listbox>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : resourceListError ? (
          <ErrorList
            className="px-4 py-6 sm:px-6"
            message={
              resourceListError?.message || "Error fetching tax Materials."
            }
            errors={resourceListError?.errors}
          />
        ) : (
          <EmptyList
            className="flex items-center justify-center px-4 py-6 sm:px-6"
            title="No tax materials"
          />
        )}
      </div>
    </div>
  );
};

export default ResourceList;
